import { useMemo, useState } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { ArrowLeft, ArrowRight } from 'lucide-react'

import { DataTable, DataTableProvider } from 'components/DataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import FbButton from 'components/FbUI/FbButton'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { cn } from 'components/UI/cn'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import {
  CampaignLeadReviewItem,
  useGetCampaignLeadReview,
  useUpdateCampaignLeadReview,
} from 'features/campaigns/campaigns_api'

interface Props {
  campaignId: number
  onBack: () => void
  onNext: () => void
}

type LeadReviewFilter = 'all' | 'included' | 'excluded'

const columnHelper = createColumnHelper<CampaignLeadReviewItem>()

function SummaryCard({
  label,
  value,
  tone,
  tooltip,
}: {
  label: string
  value: number
  tone: 'approved' | 'flagged' | 'rejected' | 'neutral'
  tooltip?: string
}) {
  const toneClasses =
    tone === 'approved'
      ? 'border-green-200 bg-green-50 text-green-700'
      : tone === 'flagged'
        ? 'border-amber-200 bg-amber-50 text-amber-700'
        : tone === 'rejected'
          ? 'border-red-200 bg-red-50 text-red-700'
          : 'border-line bg-surface text-fg'

  return (
    <div className={cn('rounded-xl border p-4', toneClasses)}>
      <p className="text-2xl font-semibold">{value}</p>
      <div className="mt-1 flex items-center gap-1.5">
        <p className="text-xs uppercase tracking-wide">{label}</p>
        {tooltip ? <HelpTooltip content={tooltip} /> : null}
      </div>
    </div>
  )
}

function StatusBadge({
  qaStatus,
}: {
  qaStatus: CampaignLeadReviewItem['qa_status']
}) {
  const isApproved = qaStatus === 'approved'
  const isFlagged = qaStatus === 'flagged'
  const isRejected = qaStatus === 'rejected'

  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize',
        isApproved && 'bg-green-50 text-green-700',
        isFlagged && 'bg-amber-50 text-amber-700',
        isRejected && 'bg-red-50 text-red-700'
      )}
    >
      {qaStatus}
    </span>
  )
}

function FilterTab({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border px-3 py-1.5 text-sm transition-colors',
        active
          ? 'border-brand bg-brand/5 text-brand'
          : 'border-line bg-surface text-fg-secondary hover:text-fg'
      )}
    >
      {label}
      <span className="ml-1.5 text-xs text-inherit/80">{count}</span>
    </button>
  )
}

function WizardReviewLeadsStepContent({ campaignId, onBack, onNext }: Props) {
  const [filter, setFilter] = useState<LeadReviewFilter>('included')
  const { data, isLoading } = useGetCampaignLeadReview(campaignId)
  const updateSelection = useUpdateCampaignLeadReview(campaignId)

  const filteredResults = useMemo(() => {
    const results = data?.results ?? []
    if (filter === 'included') {
      return results.filter((contact) => contact.is_included)
    }
    if (filter === 'excluded') {
      return results.filter((contact) => !contact.is_included)
    }
    return results
  }, [data?.results, filter])

  const columns = useMemo(
    () => [
      columnHelper.accessor('full_name', {
        header: 'Contact',
        size: 260,
        cell: (info) => (
          <div>
            <p className="text-sm font-medium text-fg truncate">
              {info.getValue()}
            </p>
            <p className="text-sm text-fg-secondary truncate">
              {info.row.original.email || 'No email'}
            </p>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'company',
        header: 'Company',
        size: 220,
        cell: (info) => (
          <div className="text-sm text-fg-secondary">
            <p className="truncate">{info.row.original.company_name}</p>
            <p className="truncate">{info.row.original.title || 'No title'}</p>
          </div>
        ),
      }),
      columnHelper.accessor('qa_status', {
        header: 'QA Status',
        size: 120,
        cell: (info) => <StatusBadge qaStatus={info.getValue()} />,
      }),
      columnHelper.accessor('qa_reason', {
        header: 'Reason',
        size: 320,
        cell: (info) => (
          <span className="text-sm text-fg-secondary">
            {info.getValue() || '-'}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'inclusion',
        header: 'Included',
        size: 100,
        cell: (info) => (
          <span
            className={cn(
              'inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize',
              info.row.original.is_included
                ? 'bg-green-50 text-green-700'
                : 'bg-gray-100 text-gray-600'
            )}
          >
            {info.row.original.is_included ? 'Included' : 'Excluded'}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        size: 140,
        meta: {
          isEditable: false,
          headerDisableOrdering: true,
          isLastColumn: true,
          rightAlign: true,
        },
        cell: (info) => {
          const isRejected = info.row.original.qa_status === 'rejected'

          if (isRejected) {
            return (
              <span className="text-sm font-medium text-fg-secondary">
                Blocked
              </span>
            )
          }

          return (
            <FbButton
              type="button"
              variant={info.row.original.is_included ? 'outline' : 'secondary'}
              size="sm"
              disabled={updateSelection.isPending}
              onClick={() =>
                updateSelection.mutate({
                  contact_ids: [info.row.original.contact_id],
                  is_included: !info.row.original.is_included,
                })
              }
            >
              {info.row.original.is_included ? 'Exclude' : 'Include'}
            </FbButton>
          )
        },
      }),
    ],
    [updateSelection]
  )

  if (isLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-fg-secondary">
        Loading contacts...
      </div>
    )
  }

  const { summary } = data
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-line bg-surface px-8 py-6">
        <div className="mx-auto max-w-[1200px]">
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-brand">
            Review Leads
          </p>
          <h1 className="text-xl font-semibold text-fg">Review Contacts</h1>
          <p className="mt-1 text-sm text-fg-secondary">
            Choose which campaign contacts should be included before previewing
            emails.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-8 py-6">
        <div className="mx-auto max-w-[1200px] space-y-6">
          <div className="grid gap-3 md:grid-cols-4">
            <SummaryCard
              label="Approved"
              value={summary.approved}
              tone="approved"
            />
            <SummaryCard
              label="Flagged"
              value={summary.flagged}
              tone="flagged"
              tooltip="Flagged contacts can still be included, but they may have recent campaign activity or another signal worth reviewing first."
            />
            <SummaryCard
              label="Rejected"
              value={summary.rejected}
              tone="rejected"
              tooltip="Rejected contacts are excluded by default because they are not safe to send to, such as personal, bounced, unsubscribed, or inactive emails."
            />
            <SummaryCard
              label="Included"
              value={summary.included}
              tone="neutral"
            />
          </div>

          <DataTableContainer>
            {summary.total === 0 ? (
              <div className="rounded-xl border border-dashed border-line bg-surface px-6 py-12 text-center">
                <h2 className="text-base font-semibold text-fg">
                  No contacts yet.
                </h2>
                <p className="mx-auto mt-2 max-w-xl text-sm text-fg-secondary">
                  Add contacts to this campaign to review recipients and preview
                  the sequence with real leads.
                </p>
              </div>
            ) : (
              <DataTable
                tableKey="campaign-review-leads-table"
                data={filteredResults}
                columns={columns}
                loading={isLoading || updateSelection.isPending}
                isPaginationEnabled={false}
                emptyTableText={{
                  title:
                    filter === 'excluded'
                      ? 'No excluded contacts.'
                      : filter === 'included'
                        ? 'No included contacts.'
                        : 'No contacts found.',
                  subtitle:
                    filter === 'excluded'
                      ? 'Contacts you exclude from this campaign will appear here.'
                      : 'Adjust your review filters or campaign contacts to see results.',
                }}
                tableHeader={
                  <div className="flex w-full flex-col gap-3 py-2">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h2 className="text-sm font-semibold text-fg">
                          Campaign Contacts
                        </h2>
                        <p className="mt-1 text-sm text-fg-secondary">
                          Approved and flagged contacts are included by default.
                          Rejected contacts are blocked and cannot be included.
                        </p>
                      </div>
                      <p className="shrink-0 text-sm text-fg-secondary">
                        {filteredResults.length} shown
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <FilterTab
                        label="Included"
                        count={summary.included}
                        active={filter === 'included'}
                        onClick={() => setFilter('included')}
                      />
                      <FilterTab
                        label="Excluded"
                        count={summary.excluded}
                        active={filter === 'excluded'}
                        onClick={() => setFilter('excluded')}
                      />
                      <FilterTab
                        label="All"
                        count={summary.total}
                        active={filter === 'all'}
                        onClick={() => setFilter('all')}
                      />
                    </div>
                  </div>
                }
              />
            )}
          </DataTableContainer>
        </div>
      </div>

      <div className="border-t border-line bg-surface px-8 py-4">
        <div className="mx-auto flex max-w-[1200px] gap-3">
          <FbButton type="button" variant="outline" onClick={onBack}>
            <ArrowLeft className="size-4" />
            Back
          </FbButton>
          <FbButton type="button" className="flex-1" onClick={onNext}>
            Continue to Build Sequence
            <ArrowRight className="size-4" />
          </FbButton>
        </div>
      </div>
    </div>
  )
}

export function WizardReviewLeadsStep(props: Props) {
  return (
    <ColumnsStoreProvider tableKey="campaign-review-leads-table">
      <DataTableProvider tableKey="campaign-review-leads-table">
        <WizardReviewLeadsStepContent {...props} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
